import { useEffect, useState } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { HV_THEME, HVHeader, ContentLoader } from "../shared/DesignSystem";

const PROVIDER_COLORS = {
  openai:  { bg: '#e8f5e9', fg: '#2e7d32', bd: '#a5d6a7' },
  azure:   { bg: '#e3f2fd', fg: '#1565c0', bd: '#90caf9' },
  helios:  { bg: '#f3e5f5', fg: '#6a1b9a', bd: '#ce93d8' },
};

function ProviderChip({ model }) {
  const provider = model.split(':')[0];
  const colors = PROVIDER_COLORS[provider] || { bg: '#f5f5f5', fg: '#555', bd: '#ddd' };
  return (
    <span style={{
      display: 'inline-block',
      background: colors.bg,
      color: colors.fg,
      border: `1px solid ${colors.bd}`,
      borderRadius: 4,
      padding: '3px 10px',
      fontSize: 12,
      fontWeight: 600,
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    }}>
      {model}
    </span>
  );
}

function Models(props) {
  const [modelFallbacks, setModelFallbacks] = useState(null);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchModelFallbacks();
  }, []);

  const fetchModelFallbacks = async () => {
    try {
      const response = await axios.get('/api/projects/modelFallbacks');
      setModelFallbacks(response.data);
    } catch {
      enqueueSnackbar('Failed to load model fallbacks', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const rows = modelFallbacks ? Object.entries(modelFallbacks) : [];

  return (
    <div style={{ background: HV_THEME.bg, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: HV_THEME.ink }}>
      <HVHeader tab="models" role={{}} env="STAGING" onTab={props.onTab} onLogout={props.onLogout} />
      <div style={{ padding: '28px 40px', maxWidth: 1440, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: HV_THEME.mute, marginBottom: 24 }}>
          <span>Models</span>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.18em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase' }}>
            Model Fallbacks
          </div>
          <div style={{ fontSize: 13, color: HV_THEME.mute, marginTop: 4 }}>
            {rows.length} models configured
          </div>
        </div>

        {loading ? (
          <ContentLoader />
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #e8e8e8' }}>
                  <th style={thStyle}>Model</th>
                  <th style={thStyle}>Backup 1</th>
                  <th style={thStyle}>Backup 2</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([model, config], idx) => (
                  <tr
                    key={model}
                    style={{ borderBottom: idx < rows.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                  >
                    <td style={tdStyle}>
                      <ProviderChip model={model} />
                    </td>
                    {[0, 1].map((i) => (
                      <td key={i} style={tdStyle}>
                        {config.backups[i]
                          ? <ProviderChip model={config.backups[i]} />
                          : <span style={{ color: HV_THEME.mute, fontSize: 12 }}>—</span>
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const thStyle = {
  textAlign: 'left',
  padding: '12px 20px',
  fontSize: 11,
  letterSpacing: '0.12em',
  fontWeight: 700,
  color: '#888',
  textTransform: 'uppercase',
};

const tdStyle = {
  padding: '14px 20px',
  fontSize: 13,
  verticalAlign: 'middle',
};

export default Models;
